import React, { useState, useEffect } from 'react';
import { adminApi, blogCategoryApi, getMediaUrl } from '../services/api';
import Pagination from './Pagination';

const AdminBlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // Filters & Search
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Form Modal State (Create / Edit)
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [categories, setCategories] = useState(['Mental Health', 'Anxiety & Stress', 'Depression', 'Relationships', 'Self-Care', 'Mindfulness', 'Career & Work', 'Personal Growth']);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    category: 'Mental Health',
    excerpt: '',
    content: '',
    featuredImage: ''
  });

  // Selected blog for View / Action Modal
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [reviewNoteModal, setReviewNoteModal] = useState(null); // { blogId, decision }
  const [reviewNoteInput, setReviewNoteInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminApi.getBlogs({ limit: 200 });
      const blogsList = response.blogs || response.data || response || [];
      setBlogs(blogsList);
    } catch (err) {
      console.error('Failed to load admin blogs:', err);
      setError(err.message || 'Failed to load blogs from server');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await blogCategoryApi.getPublicCategories();
      if (res?.categories && Array.isArray(res.categories) && res.categories.length > 0) {
        setCategories(res.categories.map((c) => c.name));
      }
    } catch (err) {
      console.warn('Could not load dynamic blog categories:', err);
    }
  };

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, []);

  const handleAddClick = () => {
    setEditingId(null);
    setFormData({
      title: '',
      author: '',
      category: 'Mental Health',
      excerpt: '',
      content: '',
      featuredImage: ''
    });
    setShowForm(true);
  };

  const handleEditClick = (blog) => {
    setEditingId(blog._id || blog.id);
    setFormData({
      title: blog.title || '',
      author: blog.createdBy?.name || blog.author || '',
      category: blog.category || 'Mental Health',
      excerpt: blog.excerpt || '',
      content: blog.content || blog.excerpt || '',
      featuredImage: blog.featuredImage || blog.image || ''
    });
    setShowForm(true);
  };

  const handleImageFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      setError('');
      const data = new FormData();
      data.append('file', file);
      const res = await adminApi.uploadBlogImage(data);
      if (res?.url) {
        setFormData((prev) => ({ ...prev, featuredImage: res.url }));
        setStatusMessage('Cover image uploaded successfully!');
        setTimeout(() => setStatusMessage(''), 3000);
      } else {
        throw new Error(res?.message || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      setError(err.message || 'Image upload failed. You can paste an image URL directly.');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.title || formData.title.trim().length < 3) {
      alert('Title must be at least 3 characters long');
      return;
    }

    const fullContent = (formData.content && formData.content.trim().length >= 10) 
      ? formData.content.trim() 
      : (formData.excerpt && formData.excerpt.trim().length >= 10) 
        ? formData.excerpt.trim() 
        : (formData.title.trim() + ' - Article description and full content details.');

    const payload = {
      title: formData.title.trim(),
      category: formData.category || 'Mental Health',
      excerpt: formData.excerpt?.trim() || fullContent.substring(0, 150),
      content: fullContent,
      featuredImage: formData.featuredImage?.trim() || ''
    };

    try {
      setIsSubmitting(true);
      setError('');

      if (editingId) {
        // Update existing blog
        const res = await adminApi.updateBlog(editingId, payload);
        const updated = res.blog || res;
        setBlogs(blogs.map(b => ((b._id === editingId || b.id === editingId) ? { ...b, ...updated, ...payload } : b)));
        setStatusMessage('Blog post updated successfully!');
      } else {
        // Create new blog
        const res = await adminApi.createBlog(payload);
        const newBlog = res.blog || res;
        setBlogs([newBlog, ...blogs]);
        setStatusMessage('New blog post created successfully!');
      }

      setTimeout(() => setStatusMessage(''), 4000);
      setShowForm(false);
      fetchBlogs();
    } catch (err) {
      console.error('Error saving blog:', err);
      const errMsg = err.message || 'Failed to save blog post';
      setError(errMsg);
      alert(`Blog creation failed: ${errMsg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Review (Approve / Reject)
  const handleReviewDecision = async (blogId, decision, note = '') => {
    try {
      setIsSubmitting(true);
      setError('');
      const response = await adminApi.reviewBlog(blogId, { decision, status: decision, reviewNote: note });
      const updatedBlog = response.blog || response;

      setBlogs(blogs.map(b => ((b._id === blogId || b.id === blogId) ? { ...b, status: decision, ...updatedBlog } : b)));
      setStatusMessage(`Blog post successfully ${decision}!`);
      setTimeout(() => setStatusMessage(''), 4000);

      setReviewNoteModal(null);
      if (selectedBlog && (selectedBlog._id === blogId || selectedBlog.id === blogId)) {
        setSelectedBlog({ ...selectedBlog, status: decision });
      }
    } catch (err) {
      console.error('Error reviewing blog:', err);
      setError(err.message || `Failed to ${decision} blog post.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Delete
  const handleDeleteClick = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;

    try {
      setIsSubmitting(true);
      await adminApi.deleteBlog(blogId);
      setBlogs(blogs.filter(b => b._id !== blogId && b.id !== blogId));
      if (selectedBlog && (selectedBlog._id === blogId || selectedBlog.id === blogId)) {
        setSelectedBlog(null);
      }
      setStatusMessage('Blog post deleted successfully!');
      setTimeout(() => setStatusMessage(''), 4000);
    } catch (err) {
      console.error('Error deleting blog:', err);
      setError(err.message || 'Failed to delete blog post');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filtered Blogs List
  const filteredBlogs = blogs.filter(b => {
    if (activeFilter !== 'all' && b.status !== activeFilter) return false;
    if (categoryFilter !== 'all' && b.category !== categoryFilter) return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const title = b.title?.toLowerCase() || '';
      const author = (b.createdBy?.name || b.author || '').toLowerCase();
      const excerpt = b.excerpt?.toLowerCase() || '';

      return title.includes(q) || author.includes(q) || excerpt.includes(q);
    }

    return true;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, categoryFilter, searchQuery]);

  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);
  const paginatedBlogs = filteredBlogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const pendingCount = blogs.filter(b => b.status === 'pending').length;
  const approvedCount = blogs.filter(b => b.status === 'approved' || b.status === 'published').length;
  const rejectedCount = blogs.filter(b => b.status === 'rejected').length;

  return (
    <div style={{ padding: '20px' }}>
      {/* Header section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
            📚 Blog & Article Management
          </h3>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
            Create, publish, edit, review, or delete mental health articles across the platform.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={fetchBlogs}
            style={{
              padding: '10px 16px',
              background: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🔄 Refresh List
          </button>

          <button
            onClick={handleAddClick}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
          >
            ➕ Create New Blog
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {statusMessage && (
        <div style={{ background: '#dcfce7', border: '1px solid #86efac', color: '#15803d', padding: '12px 18px', borderRadius: '8px', marginBottom: '20px', fontWeight: 600, fontSize: '14px' }}>
          ✓ {statusMessage}
        </div>
      )}

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c', padding: '12px 18px', borderRadius: '8px', marginBottom: '20px', fontWeight: 600, fontSize: '14px' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Filters & Search Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
        {/* Status Pills */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: `All (${blogs.length})` },
            { key: 'pending', label: `⏳ Pending Review (${pendingCount})` },
            { key: 'approved', label: `✓ Approved / Live (${approvedCount})` },
            { key: 'rejected', label: `✕ Rejected (${rejectedCount})` },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: 'none',
                fontWeight: '600',
                fontSize: '13px',
                cursor: 'pointer',
                background: activeFilter === tab.key ? '#667eea' : '#e5e7eb',
                color: activeFilter === tab.key ? '#ffffff' : '#374151',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Category & Search Filter */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              background: '#fff'
            }}
          >
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <input
            type="text"
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              width: '220px'
            }}
          />
        </div>
      </div>

      {/* CREATE / EDIT BLOG FORM MODAL */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '650px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h4 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
              {editingId ? '✏️ Edit Blog Article' : '➕ Create New Blog Article'}
            </h4>

            <form onSubmit={handleSubmitForm}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333' }}>
                  Article Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter blog article title"
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333' }}>
                    Author Name
                  </label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    placeholder="Author name (e.g. Dr. John)"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333' }}>
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      background: '#fff'
                    }}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Cover Image URL & File Upload */}
              <div style={{ marginBottom: '16px', background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                  Cover Image
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={formData.featuredImage}
                    onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                    placeholder="https://images.unsplash.com/... or upload image"
                    style={{
                      flex: 1,
                      minWidth: '220px',
                      padding: '9px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      background: '#fff',
                      outline: 'none'
                    }}
                  />

                  <label
                    style={{
                      padding: '9px 14px',
                      background: '#ede9fe',
                      color: '#6d28d9',
                      borderRadius: '6px',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: uploadingImage ? 'not-allowed' : 'pointer',
                      border: '1px solid #ddd6fe',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileUpload}
                      disabled={uploadingImage}
                      style={{ display: 'none' }}
                    />
                    {uploadingImage ? '⏳ Uploading...' : '📁 Upload Image'}
                  </label>

                  {formData.featuredImage && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, featuredImage: '' })}
                      style={{
                        padding: '9px 12px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      ✕ Clear
                    </button>
                  )}
                </div>

                {formData.featuredImage && (
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img
                      src={getMediaUrl(formData.featuredImage)}
                      alt="Cover Preview"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      style={{
                        width: '80px',
                        height: '50px',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1'
                      }}
                    />
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      Image preview loaded.
                    </span>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333' }}>
                  Excerpt / Brief Summary *
                </label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  placeholder="Brief summary displayed on article cards..."
                  required
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333' }}>
                  Full Article Content
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Write full article body text here..."
                  rows="6"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#f0f0f0',
                    color: '#333',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? 'Saving...' : editingId ? 'Update Article' : 'Publish Article'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Blogs Table */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            ⏳ Loading articles...
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Title</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Author</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Category</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Date</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBlogs.map((blog, idx) => {
                const blogId = blog._id || blog.id;
                const authorName = blog.createdBy?.name || blog.author || 'Anonymous';
                const dateStr = blog.createdAt ? new Date(blog.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : (blog.date || 'N/A');

                const statusBg = (blog.status === 'approved' || blog.status === 'published') ? '#d1fae5' : blog.status === 'rejected' ? '#fee2e2' : '#fef3c7';
                const statusColor = (blog.status === 'approved' || blog.status === 'published') ? '#059669' : blog.status === 'rejected' ? '#dc2626' : '#d97706';

                return (
                  <tr key={blogId} style={{ borderBottom: '1px solid #e5e7eb', background: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '16px', color: '#1f2937', fontWeight: '600' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {(blog.featuredImage || blog.image) && (
                          <img
                            src={getMediaUrl(blog.featuredImage || blog.image)}
                            alt=""
                            onError={(e) => { e.target.style.display = 'none'; }}
                            style={{ width: '48px', height: '36px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0, border: '1px solid #e5e7eb' }}
                          />
                        )}
                        <div>
                          <div style={{ fontSize: '15px' }}>{blog.title}</div>
                          {blog.excerpt && (
                            <div style={{ color: '#6b7280', fontSize: '13px', fontWeight: 'normal', marginTop: '2px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {blog.excerpt}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px', color: '#6b7280', fontSize: '14px' }}>{authorName}</td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: '#e0e7ff',
                        color: '#4c1d95',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {blog.category || 'Mental Health'}
                      </span>
                    </td>
                    <td style={{ padding: '16px', color: '#6b7280', fontSize: '14px' }}>{dateStr}</td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: statusBg,
                        color: statusColor,
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '700',
                        textTransform: 'capitalize'
                      }}>
                        {(blog.status === 'approved' || blog.status === 'published') ? '✓ Live' : blog.status === 'rejected' ? '✕ Rejected' : '⏳ Pending'}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setSelectedBlog(blog)}
                          style={{
                            padding: '6px 12px',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          👁️ View
                        </button>

                        <button
                          onClick={() => handleEditClick(blog)}
                          style={{
                            padding: '6px 12px',
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          ✏️ Edit
                        </button>

                        {blog.status !== 'approved' && blog.status !== 'published' && (
                          <button
                            onClick={() => handleReviewDecision(blogId, 'approved')}
                            disabled={isSubmitting}
                            style={{
                              padding: '6px 12px',
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            ✓ Approve
                          </button>
                        )}

                        {blog.status !== 'rejected' && (
                          <button
                            onClick={() => {
                              setReviewNoteModal({ blogId, decision: 'rejected' });
                              setReviewNoteInput('');
                            }}
                            disabled={isSubmitting}
                            style={{
                              padding: '6px 12px',
                              background: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            ✕ Reject
                          </button>
                        )}

                        <button
                          onClick={() => handleDeleteClick(blogId)}
                          disabled={isSubmitting}
                          style={{
                            padding: '6px 12px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && filteredBlogs.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
            No blogs found matching your criteria.
          </div>
        )}

        {!loading && filteredBlogs.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredBlogs.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(newSize) => {
              setItemsPerPage(newSize);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      {/* VIEW BLOG DETAIL MODAL */}
      {selectedBlog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '650px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h4 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
                📖 Article Preview
              </h4>
              <button
                onClick={() => setSelectedBlog(null)}
                style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '14px', color: '#374151' }}>
              <div>
                <span style={{ fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '12px', background: '#e0e7ff', color: '#4c1d95' }}>
                  {selectedBlog.category || 'Mental Health'}
                </span>
                <h3 style={{ margin: '8px 0 4px 0', fontSize: '20px', fontWeight: 800, color: '#111827' }}>
                  {selectedBlog.title}
                </h3>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  By <strong>{selectedBlog.createdBy?.name || selectedBlog.author || 'Author'}</strong> • {selectedBlog.createdAt ? new Date(selectedBlog.createdAt).toLocaleDateString() : 'Recent'}
                </div>
              </div>

              {(selectedBlog.featuredImage || selectedBlog.image) && (
                <img
                  src={getMediaUrl(selectedBlog.featuredImage || selectedBlog.image)}
                  alt={selectedBlog.title}
                  style={{ width: '100%', height: '220px', objectFit: 'cover', borderRadius: '8px' }}
                />
              )}

              <div>
                <label style={{ fontWeight: '600', color: '#1f2937', display: 'block', marginBottom: '4px' }}>Summary / Excerpt</label>
                <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '13.5px', lineHeight: 1.5 }}>
                  {selectedBlog.excerpt}
                </div>
              </div>

              {selectedBlog.content && (
                <div>
                  <label style={{ fontWeight: '600', color: '#1f2937', display: 'block', marginBottom: '4px' }}>Full Article Body</label>
                  <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                    {selectedBlog.content}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                type="button"
                onClick={() => setSelectedBlog(null)}
                style={{
                  padding: '10px 20px',
                  background: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>

              <button
                type="button"
                onClick={() => {
                  const b = selectedBlog;
                  setSelectedBlog(null);
                  handleEditClick(b);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#6366f1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ✏️ Edit Article
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REJECTION REASON MODAL */}
      {reviewNoteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h4 style={{ marginBottom: '12px', fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
              Reject Blog Article
            </h4>
            <p style={{ marginBottom: '16px', fontSize: '14px', color: '#6b7280' }}>
              Provide feedback explaining why this blog article is rejected.
            </p>

            <textarea
              value={reviewNoteInput}
              onChange={(e) => setReviewNoteInput(e.target.value)}
              placeholder="Reason for rejection or requested edits..."
              rows="4"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                marginBottom: '20px',
                fontFamily: 'inherit'
              }}
            ></textarea>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setReviewNoteModal(null)}
                style={{
                  padding: '10px 20px',
                  background: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleReviewDecision(reviewNoteModal.blogId, 'rejected', reviewNoteInput)}
                disabled={isSubmitting}
                style={{
                  padding: '10px 20px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogManagement;
